import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

interface NotificationSettings {
  pushEnabled: boolean;
  matchingAlert: boolean;
  chatAlert: boolean;
  serviceNotice: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'app_notification_settings';

const defaultSettings: NotificationSettings = {
  pushEnabled: true,
  matchingAlert: true,
  chatAlert: true,
  serviceNotice: true,
  marketing: false,
};

function loadSettings(): NotificationSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultSettings, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...defaultSettings };
}

function saveSettings(settings: NotificationSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export default function AppNotificationSettingsPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  function update(key: keyof NotificationSettings, value: boolean) {
    const next = { ...settings, [key]: value };
    if (key === 'pushEnabled' && !value) {
      next.matchingAlert = false;
      next.chatAlert = false;
      next.serviceNotice = false;
      next.marketing = false;
    }
    setSettings(next);
    saveSettings(next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  const toggleOptions = [
    {
      key: 'matchingAlert' as const,
      icon: 'ri-hand-heart-line',
      label: '매칭 알림',
      desc: '새로운 매칭 요청, 수락, 상태 변경 시 알림',
      color: '#2E7D6B',
      bg: 'bg-[#E8F5E9]',
    },
    {
      key: 'chatAlert' as const,
      icon: 'ri-chat-3-line',
      label: '채팅 메시지 알림',
      desc: '새로운 채팅 메시지 도착 시 실시간 알림',
      color: '#1976D2',
      bg: 'bg-[#E3F2FD]',
    },
    {
      key: 'serviceNotice' as const,
      icon: 'ri-megaphone-line',
      label: '서비스 공지',
      desc: '공지사항, 이용안내 등 중요 정보 알림',
      color: '#F57C00',
      bg: 'bg-[#FFF3E0]',
    },
    {
      key: 'marketing' as const,
      icon: 'ri-gift-line',
      label: '이벤트·혜택',
      desc: '프로모션, 이벤트, 혜택 정보 알림',
      color: '#E91E63',
      bg: 'bg-[#FCE4EC]',
    },
  ];

  if (auth.loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!auth.isAuthenticated) {
    return null;
  }

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-[#F8F9FA]">
        {/* 헤더 */}
        <div className="px-5 pt-6 pb-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">알림 설정</h1>
            <p className="text-xs text-gray-400">푸시 알림 수신 여부를 설정하세요</p>
          </div>
        </div>

        <div className="px-5 space-y-5">
          {/* 마스터 토글 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
                  <i className="ri-notification-3-line text-xl text-[#2E7D6B]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">푸시 알림</p>
                  <p className="text-xs text-gray-400">
                    {settings.pushEnabled ? '모든 알림이 켜져 있습니다' : '모든 알림이 꺼져 있습니다'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => update('pushEnabled', !settings.pushEnabled)}
                className={`relative w-[52px] h-[31px] rounded-full cursor-pointer transition-colors duration-200 ${
                  settings.pushEnabled ? 'bg-[#2E7D6B]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-[3px] w-[25px] h-[25px] rounded-full bg-white shadow-sm transition-all duration-200 ${
                    settings.pushEnabled ? 'left-[24px]' : 'left-[3px]'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* 세부 토글 */}
          <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden transition-opacity duration-200 ${settings.pushEnabled ? '' : 'opacity-40 pointer-events-none'}`}>
            <div className="px-5 py-3 border-b border-gray-50">
              <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">알림 유형별 설정</p>
            </div>
            {toggleOptions.map((opt, i) => (
              <div
                key={opt.key}
                className={`flex items-center justify-between px-5 py-4 ${i < toggleOptions.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${opt.bg} shrink-0`}>
                    <i className={`${opt.icon} text-sm`} style={{ color: opt.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                    <p className="text-[11px] text-gray-400">{opt.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => update(opt.key, !settings[opt.key])}
                  className={`relative w-[44px] h-[26px] rounded-full cursor-pointer transition-colors duration-200 shrink-0 ${
                    settings[opt.key] ? 'bg-[#2E7D6B]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-[2px] w-[22px] h-[22px] rounded-full bg-white shadow-sm transition-all duration-200 ${
                      settings[opt.key] ? 'left-[20px]' : 'left-[2px]'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {/* 저장됨 표시 */}
          <div className={`text-center transition-all duration-300 ${saved ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8F5E9] text-xs font-medium text-[#2E7D6B]">
              <i className="ri-check-line text-sm" />
              설정이 저장되었습니다
            </span>
          </div>

          {/* 안내 */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-amber-500" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-800 mb-1.5">알림 설정 안내</p>
                <ul className="text-[11px] text-gray-500 space-y-1">
                  <li>· 푸시 알림을 끄면 모든 알림 유형이 중단됩니다</li>
                  <li>· 채팅 메시지 알림을 끄면 실시간 메시지 도착 알림을 받지 않습니다</li>
                  <li>· 앱 내 공지사항은 알림 설정과 관계없이 확인 가능합니다</li>
                  <li>· 중요 서비스 변경사항은 알림 설정과 관계없이 이메일/SMS로 안내될 수 있습니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}